import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useFetchMenu from '../../Hooks/UseFetchMenu';
import { API_BASE_URL } from '../../config';
import './Admin.module.css';

// Credenciales de administrador
const ADMIN_CREDENTIALS = {
  username: 'admin',
  password: 'bliss2024'
};

// Los ejes de vino se cargan como dropdown (no numero libre) para que sea
// facil de completar: cada opcion mapea a un valor 0-100 (o 1-4 para funk)
// que sigue alimentando el mapa de sabores igual que antes.
const BODY_OPTIONS = [
  { value: '', label: 'Sin definir' },
  { value: '20', label: 'Ligero' },
  { value: '50', label: 'Medio' },
  { value: '85', label: 'Con cuerpo' },
];

const ACIDITY_OPTIONS = [
  { value: '', label: 'Sin definir' },
  { value: '1', label: '1 · Muy suave' },
  { value: '2', label: '2 · Suave' },
  { value: '3', label: '3 · Equilibrada' },
  { value: '4', label: '4 · Viva' },
  { value: '5', label: '5 · Muy viva' },
];

const SWEETNESS_OPTIONS = [
  { value: '', label: 'Sin definir' },
  { value: '20', label: 'Seco' },
  { value: '50', label: 'Semi dulce' },
  { value: '85', label: 'Dulce' },
];

const STRENGTH_OPTIONS = [
  { value: '', label: 'Sin definir' },
  { value: '20', label: 'Suave' },
  { value: '50', label: 'Media' },
  { value: '85', label: 'Fuerte' },
];

const snapToBucket = (value, buckets) => {
  if (value === undefined || value === null || value === '') return '';
  const num = Number(value);
  if (Number.isNaN(num)) return '';
  let closest = buckets[0];
  let minDiff = Math.abs(num - closest);
  buckets.forEach((b) => {
    const diff = Math.abs(num - b);
    if (diff < minDiff) {
      minDiff = diff;
      closest = b;
    }
  });
  return String(closest);
};

const rowFromProduct = (product) => ({
  isRecommended: product.isRecommended || product.recommended || false,
  isMostOrdered: product.isMostOrdered || product.mostOrdered || false,
  isSignature: product.isSignature || product.signature || false,
  isShareable: product.isShareable || product.shareable || false,
  isProductOfDay: product.isProductOfDay || product.productOfDay || false,
  pairingSuggestion: product.pairingSuggestion || product.pairing_suggestion || product.suggestion || '',
  acidity: snapToBucket(product.acidity, [1, 2, 3, 4, 5]),
  intensity: snapToBucket(product.intensity, [20, 50, 85]),
  funkiness: product.funkiness ?? '',
  wineType: product.wineType || product.wine_type || product.color || '',
  sweetness: snapToBucket(product.sweetness, [20, 50, 85]),
  strength: snapToBucket(product.strength, [20, 50, 85]),
  family: product.family || '',
});

const Admin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [rows, setRows] = useState({}); // { [productId]: rowData }
  const [savingRowId, setSavingRowId] = useState(null);
  const [rowMessages, setRowMessages] = useState({}); // { [productId]: {type, text} }
  const [refreshKey, setRefreshKey] = useState(0);

  const { menuItems, loading } = useFetchMenu(API_BASE_URL, refreshKey);

  // Actualizar categoría seleccionada cuando se refrescan los datos
  useEffect(() => {
    if (selectedCategory && menuItems.length > 0) {
      const updatedCategory = menuItems.find(cat => cat.id === selectedCategory.id);
      if (updatedCategory) {
        setSelectedCategory(updatedCategory);
      }
    }
  }, [menuItems, refreshKey, selectedCategory]);

  // Inicializar filas editables cuando cambia la categoría seleccionada
  useEffect(() => {
    if (!selectedCategory) {
      setRows({});
      return;
    }
    const initial = {};
    (selectedCategory.products || []).forEach((product) => {
      initial[product.id] = rowFromProduct(product);
    });
    setRows(initial);
    setRowMessages({});
  }, [selectedCategory?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Verificar autenticación al cargar
  useEffect(() => {
    const auth = localStorage.getItem('admin_authenticated');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === ADMIN_CREDENTIALS.username && password === ADMIN_CREDENTIALS.password) {
      setIsAuthenticated(true);
      localStorage.setItem('admin_authenticated', 'true');
      setError('');
    } else {
      setError('Credenciales incorrectas');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('admin_authenticated');
    setUsername('');
    setPassword('');
    setSelectedCategory(null);
    setRows({});
  };

  const updateRow = useCallback((productId, field, value) => {
    setRows((prev) => ({
      ...prev,
      [productId]: { ...prev[productId], [field]: value },
    }));
  }, []);

  const handleSaveRow = async (productId) => {
    const rowData = rows[productId];
    if (!rowData) return;

    setSavingRowId(productId);
    setRowMessages((prev) => ({ ...prev, [productId]: { type: '', text: '' } }));

    try {
      const updateData = {
        isRecommended: rowData.isRecommended || false,
        isMostOrdered: rowData.isMostOrdered || false,
        isSignature: rowData.isSignature || false,
        isShareable: rowData.isShareable || false,
        isProductOfDay: rowData.isProductOfDay || false,
        pairingSuggestion: rowData.pairingSuggestion || null,
        acidity: rowData.acidity === '' ? null : Number(rowData.acidity),
        intensity: rowData.intensity === '' ? null : Number(rowData.intensity),
        funkiness: rowData.funkiness === '' ? null : Number(rowData.funkiness),
        wineType: rowData.wineType || null,
        sweetness: rowData.sweetness === '' ? null : Number(rowData.sweetness),
        strength: rowData.strength === '' ? null : Number(rowData.strength),
        family: rowData.family || null,
      };

      const apiRoot = API_BASE_URL.replace(/\/$/, '');
      const response = await fetch(`${apiRoot}/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }

      setRowMessages((prev) => ({ ...prev, [productId]: { type: 'success', text: 'Guardado' } }));
      setRefreshKey((prev) => prev + 1);
      setTimeout(() => {
        setRowMessages((prev) => ({ ...prev, [productId]: { type: '', text: '' } }));
      }, 2000);
    } catch (err) {
      setRowMessages((prev) => ({
        ...prev,
        [productId]: { type: 'error', text: err.message || 'Error al guardar' },
      }));
    } finally {
      setSavingRowId(null);
    }
  };

  const handleSaveAll = async () => {
    const ids = Object.keys(rows);
    for (const id of ids) {
      // eslint-disable-next-line no-await-in-loop
      await handleSaveRow(id);
    }
  };

  // Filtrar categorías normales (excluir la categoría de destacados)
  const normalCategories = menuItems.filter(cat => cat.id !== 9999999);
  const isWineCategory = (selectedCategory?.name || '').toLowerCase().includes('vino');
  const cocktailCategoryTerms = ['cocktail', 'mocktail', 'tragos frozen'];
  const isCocktailCategory = cocktailCategoryTerms.some(term =>
    (selectedCategory?.name || '').toLowerCase().includes(term)
  );

  if (!isAuthenticated) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-box">
          <h2>Panel de Administración</h2>
          <form onSubmit={handleLogin}>
            <div className="admin-form-group">
              <label>Usuario:</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="admin-form-group">
              <label>Contraseña:</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && <div className="admin-error">{error}</div>}
            <button type="submit" className="admin-login-button">
              Iniciar Sesión
            </button>
          </form>
          <button
            onClick={() => navigate('/Home')}
            className="admin-back-button"
          >
            Volver al Menú
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="admin-loading">Cargando...</div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Panel de Administración - BLISS</h1>
        <button onClick={handleLogout} className="admin-logout-button">
          Cerrar Sesión
        </button>
      </div>

      <div className="admin-content">
        <div className="admin-sidebar">
          <h3>Índice</h3>
          <div className="admin-categories-list">
            {normalCategories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category)}
                className={`admin-category-button ${selectedCategory?.id === category.id ? 'active' : ''}`}
              >
                <span>{category.name}</span>
                <span style={{ opacity: 0.4, marginLeft: '8px' }}>({category.products?.length || 0})</span>
              </button>
            ))}
          </div>
        </div>

        <div className="admin-main">
          {selectedCategory ? (
            <>
              <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                  <h2 style={{ marginBottom: '8px' }}>{selectedCategory.name}</h2>
                  <p style={{
                    fontSize: '14px',
                    color: '#666',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    margin: 0
                  }}>
                    {selectedCategory.products?.length || 0} productos
                  </p>
                </div>
                <button
                  onClick={handleSaveAll}
                  className="admin-save-button"
                  disabled={savingRowId !== null}
                  style={{ marginBottom: '4px' }}
                >
                  {savingRowId !== null ? 'Guardando...' : 'Guardar todo'}
                </button>
              </div>

              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th className="admin-table-sticky">Producto</th>
                      <th>⭐</th>
                      <th>🔥</th>
                      <th>✨</th>
                      <th>👥</th>
                      <th>🌟</th>
                      <th>Maridaje</th>
                      {isWineCategory && (
                        <>
                          <th>Tipo</th>
                          <th>Acidez</th>
                          <th>Cuerpo</th>
                          <th>Funk</th>
                        </>
                      )}
                      {isCocktailCategory && (
                        <>
                          <th>Familia</th>
                          <th>Dulzor</th>
                          <th>Fuerza</th>
                        </>
                      )}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedCategory.products || []).map((product) => {
                      const row = rows[product.id] || rowFromProduct(product);
                      const message = rowMessages[product.id];
                      const isSaving = savingRowId === product.id;
                      return (
                        <tr key={product.id}>
                          <td className="admin-table-sticky">
                            <div className="admin-table-product-name">{product.name}</div>
                            <div className="admin-table-product-price">{(product.price / 100).toFixed(2)} €</div>
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={row.isRecommended || false}
                              onChange={(e) => updateRow(product.id, 'isRecommended', e.target.checked)}
                            />
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={row.isMostOrdered || false}
                              onChange={(e) => updateRow(product.id, 'isMostOrdered', e.target.checked)}
                            />
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={row.isSignature || false}
                              onChange={(e) => updateRow(product.id, 'isSignature', e.target.checked)}
                            />
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={row.isShareable || false}
                              onChange={(e) => updateRow(product.id, 'isShareable', e.target.checked)}
                            />
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={row.isProductOfDay || false}
                              onChange={(e) => updateRow(product.id, 'isProductOfDay', e.target.checked)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="admin-table-text"
                              value={row.pairingSuggestion || ''}
                              onChange={(e) => updateRow(product.id, 'pairingSuggestion', e.target.value)}
                              placeholder="Ej: tabla de quesos"
                            />
                          </td>
                          {isWineCategory && (
                            <>
                              <td>
                                <select
                                  className="admin-table-select"
                                  value={row.wineType || ''}
                                  onChange={(e) => updateRow(product.id, 'wineType', e.target.value)}
                                >
                                  <option value="">Inferir</option>
                                  <option value="blanco">Blanco</option>
                                  <option value="orange">Orange</option>
                                  <option value="tinto">Tinto</option>
                                  <option value="rosado">Rosado</option>
                                  <option value="petnat">Pet Nat</option>
                                </select>
                              </td>
                              <td>
                                <select
                                  className="admin-table-select"
                                  value={row.acidity}
                                  onChange={(e) => updateRow(product.id, 'acidity', e.target.value)}
                                >
                                  {ACIDITY_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <select
                                  className="admin-table-select"
                                  value={row.intensity}
                                  onChange={(e) => updateRow(product.id, 'intensity', e.target.value)}
                                >
                                  {BODY_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  className="admin-table-number"
                                  value={row.funkiness}
                                  onChange={(e) => updateRow(product.id, 'funkiness', e.target.value)}
                                  placeholder="0–100"
                                />
                              </td>
                            </>
                          )}
                          {isCocktailCategory && (
                            <>
                              <td>
                                <select
                                  className="admin-table-select"
                                  value={row.family || ''}
                                  onChange={(e) => updateRow(product.id, 'family', e.target.value)}
                                >
                                  <option value="">Sin definir</option>
                                  <option value="refrescante">Refrescante</option>
                                  <option value="fuerte">Fuerte</option>
                                  <option value="dulce">Dulce</option>
                                  <option value="clasico">Clásico</option>
                                  <option value="amargo">Amargo</option>
                                </select>
                              </td>
                              <td>
                                <select
                                  className="admin-table-select"
                                  value={row.sweetness}
                                  onChange={(e) => updateRow(product.id, 'sweetness', e.target.value)}
                                >
                                  {SWEETNESS_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <select
                                  className="admin-table-select"
                                  value={row.strength}
                                  onChange={(e) => updateRow(product.id, 'strength', e.target.value)}
                                >
                                  {STRENGTH_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </td>
                            </>
                          )}
                          <td>
                            <button
                              onClick={() => handleSaveRow(product.id)}
                              className="admin-table-save-button"
                              disabled={isSaving}
                            >
                              {isSaving ? '...' : 'Guardar'}
                            </button>
                            {message?.text && (
                              <div className={`admin-table-message ${message.type === 'success' ? 'ok' : 'err'}`}>
                                {message.text}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {isWineCategory && (
                <p style={{ fontSize: '12px', color: '#666', marginTop: '16px' }}>
                  Acidez y cuerpo se eligen de una lista. Funk es un número libre de 0 a 100 (se convierte a los 4 rayos en pantalla). Tipo "Inferir" deduce el color a partir del nombre.
                </p>
              )}
              {isCocktailCategory && (
                <p style={{ fontSize: '12px', color: '#666', marginTop: '16px' }}>
                  Dulzor y fuerza se eligen de una lista. La descripción con los ingredientes ya cargada se muestra tal cual en la tarjeta del mapa.
                </p>
              )}
            </>
          ) : (
            <div className="admin-welcome">
              <h2>Bienvenido al Panel de Administración</h2>
              <p>Selecciona una categoría del menú lateral para gestionar los productos destacados.</p>
              <div className="admin-info">
                <h3>Funcionalidades:</h3>
                <ul>
                  <li>✅ Gestionar badges de productos (Recomendado, Más pedido, Signature, etc.)</li>
                  <li>✅ Agregar o editar sugerencias de maridaje</li>
                  <li>✅ Gestionar datos del mapa de sabores (acidez, cuerpo, funk)</li>
                  <li>✅ Editar todos los productos de la categoría en una sola tabla</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
